// Copyright ©️ 2026 eVolve MEP, LLC

import type {
  AutodeskElementTag,
  AutodeskElementTagId,
  AutodeskElementTagPlacement,
  CreateAutodeskElementTagRequest,
  DeepReadonly,
  SaveAutodeskElementTagsRequest,
} from '@evolvemep/api-ts';
import { v4 as uuid } from 'uuid';

import type { SelectedElementProperty } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/types';
import type { ForgeViewerSelection } from 'components/Autodesk/extensions/SelectionToolsExtension/types';
import { getBasicStoreListeners } from 'helpers/getBasicStoreListeners';
import { isNil } from 'helpers/isNotNil';
import { strongIsEqual } from 'helpers/strongEntries';

export type ElementTagsSnapshot = {
  /** Tags as last confirmed by the backend — the "clean" state to restore on cancel. */
  committedTags: AutodeskElementTag[] | null;
  /** Current working copy; may include pending creates, updates, and removes. */
  draftTags: AutodeskElementTag[] | null;
  /** `true` when `draftTags` differs from `committedTags`. */
  isDirty: boolean;
  /** The persistence handlers or `null` if none are registered. */
  persistence: ElementTagsPersistence | null;
  /** Whether tag labels and leaders are currently visible in the viewer. */
  tagsVisible: boolean;
  /** `true` while a save is in progress. */
  isSaving: boolean;
};

/** Backend persistence implemented by the parent React tree (which owns the API hooks). */
type ElementTagsPersistence = {
  /** Batch-save all staged creates, updates, and deletes in a single request. */
  saveAll: (request: SaveAutodeskElementTagsRequest) => Promise<AutodeskElementTag[]>;
};

/** A client-side provisional ID for a tag that has not yet been persisted. */
const makeProvisionalId = (): AutodeskElementTagId => `tmp_${uuid()}` as AutodeskElementTagId;
const isProvisionalId = (id: AutodeskElementTagId): boolean => id.startsWith('tmp_');

export const createElementTagsStore = ({ initialVisible = false }: { initialVisible?: boolean } = {}) => {
  let snapshot: ElementTagsSnapshot = {
    committedTags: null,
    draftTags: null,
    isDirty: false,
    persistence: null,
    tagsVisible: initialVisible,
    isSaving: false,
  };

  const { subscribe, notifyListeners } = getBasicStoreListeners();

  const updateSnapshot = (patch: Partial<ElementTagsSnapshot>) => {
    snapshot = { ...snapshot, ...patch };
    notifyListeners();
  };

  /**
   * Determine whether `draft` differs from `committed` in any meaningful way.
   * Dirty when: a tag has a provisional id, a tag was removed, or a tag's properties/placement changed.
   */
  const computeIsDirty = (committed: AutodeskElementTag[] | null, draft: AutodeskElementTag[] | null): boolean => {
    if (committed === null && draft === null) return false;
    if (committed === null || draft === null) return true;
    if (committed.length !== draft.length) return true;
    const committedMap = new Map(committed.map((t) => [t.elementTagId, t]));
    // Check if the draft elements have any differences from the committed elements.
    for (const t of draft) {
      if (isProvisionalId(t.elementTagId)) return true;
      const c = committedMap.get(t.elementTagId);
      if (!c) return true;
      if (!strongIsEqual(t, c)) return true;
    }
    // Check if there are any committed elements that are missing from the draft (i.e., removed).
    const draftIds = new Set(draft.map((t) => t.elementTagId));
    for (const c of committed) {
      if (!draftIds.has(c.elementTagId)) return true;
    }
    return false;
  };

  const setDraftTags = (draftTags: AutodeskElementTag[]) => {
    updateSnapshot({ draftTags, isDirty: computeIsDirty(snapshot.committedTags, draftTags) });
  };

  const replaceDraftTag = (updated: AutodeskElementTag) =>
    setDraftTags((snapshot.draftTags ?? []).map((t) => (t.elementTagId === updated.elementTagId ? updated : t)));

  return {
    getSnapshot: () => snapshot,
    subscribe,

    clear: () => updateSnapshot({ committedTags: null, draftTags: null, isDirty: false }),

    registerPersistence: (persistence: ElementTagsPersistence) => {
      updateSnapshot({ persistence });
    },
    unregisterPersistence: () => {
      updateSnapshot({ persistence: null });
    },

    /** Toggle tag label/leader visibility in the viewer and update the snapshot. */
    setTagsVisible: (visible: boolean) => {
      updateSnapshot({ tagsVisible: visible });
    },

    /**
     * Replace all rendered and committed tags with `tags` (called after the initial backend load
     * and after a successful save or cancel).
     */
    load: (tags: AutodeskElementTag[]) => {
      updateSnapshot({ committedTags: tags, draftTags: tags, isDirty: false });
    },

    /**
     * Stage a new property on the current selection.
     * Returns the affected tags, or `null` if nothing was selected
     */
    addFromSelection: (property: SelectedElementProperty['name'], selection: DeepReadonly<ForgeViewerSelection[]>) => {
      if (selection.length === 0) return null;

      const currentDraft = snapshot.draftTags ?? [];
      const existingByElementId = new Map(currentDraft.map((t) => [t.elementId, t]));

      const toUpdate: AutodeskElementTag[] = [];
      const toCreate: AutodeskElementTag[] = [];

      for (const { externalId: elementId } of selection) {
        const existing = existingByElementId.get(elementId);
        if (!isNil(existing)) {
          if (!existing.properties.includes(property)) {
            toUpdate.push({ ...existing, properties: [...existing.properties, property] });
          }
        } else {
          toCreate.push({
            elementTagId: makeProvisionalId(),
            elementId,
            properties: [property],
          });
        }
      }

      const updatedIds = new Set(toUpdate.map((t) => t.elementTagId));
      const newDraft = [
        ...currentDraft.map((t) =>
          updatedIds.has(t.elementTagId) ? (toUpdate.find((u) => u.elementTagId === t.elementTagId) ?? t) : t,
        ),
        ...toCreate,
      ];
      setDraftTags(newDraft);
      return [...toUpdate, ...toCreate];
    },

    /**
     * Stage the removal of a property from all tags belonging to the current selection.
     * Returns the affected (updated) tags, or `null` if nothing was selected
     */
    removeFromSelection: (
      property: SelectedElementProperty['name'],
      selection: DeepReadonly<ForgeViewerSelection[]>,
    ) => {
      if (selection.length === 0) return null;

      const selectedElementIds = new Set(selection.map((r) => r.externalId));
      const currentDraft = snapshot.draftTags ?? [];

      const toUpdate: AutodeskElementTag[] = [];
      const toRemoveIds = new Set<AutodeskElementTagId>();

      for (const tag of currentDraft) {
        if (!selectedElementIds.has(tag.elementId)) continue;
        if (!tag.properties.includes(property)) continue;

        const newProperties = tag.properties.filter((p) => p !== property);
        if (newProperties.length === 0) {
          toRemoveIds.add(tag.elementTagId);
        } else {
          toUpdate.push({ ...tag, properties: newProperties });
        }
      }

      if (toRemoveIds.size === 0 && toUpdate.length === 0) return null;

      const updatedIds = new Set(toUpdate.map((t) => t.elementTagId));
      const newDraft = currentDraft
        .filter((t) => !toRemoveIds.has(t.elementTagId))
        .map((t) =>
          updatedIds.has(t.elementTagId) ? (toUpdate.find((u) => u.elementTagId === t.elementTagId) ?? t) : t,
        );

      setDraftTags(newDraft);
      return toUpdate;
    },

    /**
     * Stage the removal of every tag belonging to the current selection, regardless of properties.
     * Returns the removed tags, or `null` if nothing was selected.
     */
    removeAllFromSelection: (selection: DeepReadonly<ForgeViewerSelection[]>) => {
      const selectedElementIds = new Set(selection.map((r) => r.externalId));
      const currentDraft = snapshot.draftTags ?? [];

      const toRemove = currentDraft.filter((tag) => selectedElementIds.has(tag.elementId));
      if (toRemove.length === 0) return null;

      const toRemoveIds = new Set(toRemove.map((t) => t.elementTagId));
      setDraftTags(currentDraft.filter((t) => !toRemoveIds.has(t.elementTagId)));
      return toRemove;
    },

    /**
     * Stage the dragged label's new head position.
     * The `TagRenderingManager` has already moved the label visually.
     * Nothing is persisted until `save()` is called.
     */
    commitPlacement: (id: AutodeskElementTagId, placement: AutodeskElementTagPlacement) => {
      const existing = (snapshot.draftTags ?? []).find((t) => t.elementTagId === id);
      if (isNil(existing)) return;
      replaceDraftTag({ ...existing, placement });
    },

    /**
     * Persist all staged changes in one batch request.
     * On success, `committedTags` is updated to the server's response and `isDirty` is cleared.
     * On failure the draft is preserved so the user can retry or cancel.
     */
    save: async () => {
      const committed = snapshot.committedTags ?? [];
      const draft = snapshot.draftTags ?? [];
      const committedMap = new Map(committed.map((t) => [t.elementTagId, t]));
      const draftIds = new Set(draft.map(({ elementTagId }) => elementTagId));

      const creates: CreateAutodeskElementTagRequest[] = draft
        .filter((t) => isProvisionalId(t.elementTagId))
        .map(({ elementId, properties, placement }) => ({ elementId, properties, placement }));

      const updates = draft
        .filter((t) => !isProvisionalId(t.elementTagId))
        .filter(({ elementTagId, properties, placement }) => {
          const c = committedMap.get(elementTagId);
          if (!c) return false;
          return !strongIsEqual(c.properties, properties) || !strongIsEqual(c.placement, placement);
        });

      const deletes = committed
        .filter(({ elementTagId }) => !draftIds.has(elementTagId))
        .map(({ elementTagId }) => elementTagId);

      if (isNil(snapshot.persistence)) throw new Error('ElementTagsStore persistence not yet registered');
      updateSnapshot({ isSaving: true });
      try {
        const savedTags = await snapshot.persistence.saveAll({ creates, updates, deletes });
        updateSnapshot({ committedTags: savedTags, draftTags: savedTags, isDirty: false });
      } finally {
        updateSnapshot({ isSaving: false });
      }
    },

    /**
     * Discard all staged changes and restore the last committed state in the viewer.
     */
    cancel: () => {
      const committed = snapshot.committedTags ?? [];
      updateSnapshot({ draftTags: committed, isDirty: false });
    },
  };
};

export type ElementTagsStore = ReturnType<typeof createElementTagsStore>;
